"use client";

import {
  Dispatch,
  createContext,
  useReducer,
  useTransition,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from "react";
import { IModalActions } from "@/types/modal";
import type { IModalState, IModalAction } from "@/types/modal";
import {
  canOpenLeadModalSync,
  prepareLeadModalOpen,
} from "@/helpers/leadModalGate";

const modalInitState: IModalState = {
  isModalForceClose: false,
  isModalOpen: false,
  modalType: null,
  modalData: {},
};

/** Dispatch-only context — subscribers do not re-render when modal state changes. */
export const ModalDispatchContext = createContext<Dispatch<IModalAction>>(
  () => null,
);

/** State-only context — for components that read modal state. */
export const ModalStateContext = createContext<IModalState>(modalInitState);

/**
 * @deprecated Prefer useModalDispatch / useModalState to avoid unnecessary re-renders.
 * Kept for backward compatibility with existing consumers.
 */
export const ModalContext = createContext<{
  modalState: IModalState;
  modalDispatch: Dispatch<IModalAction>;
}>({
  modalDispatch: () => null,
  modalState: modalInitState,
});

export function useModalDispatch() {
  return useContext(ModalDispatchContext);
}

export function useModalState() {
  return useContext(ModalStateContext);
}

/** Legacy combined hook — re-renders on any modal state change. */
export function useModalContext() {
  return useContext(ModalContext);
}

function reducer(state: IModalState, action: IModalAction): IModalState {
  switch (action.type) {
    case IModalActions.OpenModal:
      return { ...state, isModalOpen: true, ...action.data };
    case IModalActions.CloseModal:
      return { isModalOpen: false };
    case IModalActions.ToggleModal:
      if (!state.isModalOpen) {
        return { ...state, isModalOpen: false, ...action.data };
      }
      return { isModalOpen: false };
    case IModalActions.UpdateModal:
      return { ...state, ...action.data };
    default:
      return state;
  }
}

export default function ModalContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [modalState, dispatch] = useReducer(reducer, modalInitState);
  const [, startTransition] = useTransition();

  // Browser Back / bfcache can restore an open thank-you modal; always clear it.
  useEffect(() => {
    const closeModal = () => {
      dispatch({ type: IModalActions.CloseModal });
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) closeModal();
    };

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("popstate", closeModal);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("popstate", closeModal);
    };
  }, []);

  const modalDispatch = useCallback(
    (action: IModalAction) => {
      // Open only after host + body chunks are ready so the dialog paints
      // fully formed (no empty→full flicker from per-modal code-splitting).
      if (action.type === IModalActions.OpenModal) {
        const modalType = action.data?.modalType ?? null;
        if (canOpenLeadModalSync(modalType)) {
          dispatch(action);
          return;
        }
        void prepareLeadModalOpen(modalType).then(() => {
          // Flush host into CommonModalsLazy before opening so the first
          // paint includes ShareBanner / form body (no empty dialog frame).
          dispatch(action);
        });
        return;
      }
      // UpdateModal can also flip isModalOpen (e.g. 30s popup).
      if (
        action.type === IModalActions.UpdateModal &&
        action.data?.isModalOpen === true
      ) {
        const modalType = action.data?.modalType ?? null;
        if (canOpenLeadModalSync(modalType)) {
          startTransition(() => {
            dispatch(action);
          });
          return;
        }
        void prepareLeadModalOpen(modalType).then(() => {
          startTransition(() => {
            dispatch(action);
          });
        });
        return;
      }
      startTransition(() => {
        dispatch(action);
      });
    },
    [startTransition],
  );

  const legacyValue = useMemo(
    () => ({ modalState, modalDispatch }),
    [modalState, modalDispatch],
  );

  return (
    <ModalDispatchContext.Provider value={modalDispatch}>
      <ModalStateContext.Provider value={modalState}>
        <ModalContext.Provider value={legacyValue}>
          {children}
        </ModalContext.Provider>
      </ModalStateContext.Provider>
    </ModalDispatchContext.Provider>
  );
}
